import { useNavVisibility } from '@/hooks/use-nav-visibility'
import { enableCoaching, enableSkillsMap, enableSupport } from '@/lib/feature-flags'
import { cn } from '@/lib/utils'
import { useTabNotificationsStore } from '@/stores/tab-notifications.store'
import {
  Bot,
  Briefcase,
  CalendarDays,
  FileText,
  GraduationCap,
  Heart,
  LifeBuoy,
  Map as MapIcon,
  MessageCircle,
  User,
} from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'

interface Tab {
  name: string
  href: string
  icon: typeof MessageCircle
  alwaysVisible: boolean
  notificationKey?: 'jobs' | 'skills'
}

// Outplacement features — switched on only once severance has begun. For a
// severance-cohort member who hasn't started yet, these are hidden until their
// severance start date; regular embrace users are never "severance pending", so
// their nav is unchanged.
const OUTPLACEMENT_HREFS = new Set(['/chat', '/resume', '/opportunities', '/agent', '/learn'])

const tabs: Tab[] = [
  { name: 'Coach', href: '/chat', icon: MessageCircle, alwaysVisible: true },
  { name: 'Resume', href: '/resume', icon: FileText, alwaysVisible: true },
  {
    name: 'Skills',
    href: '/skills-map',
    icon: MapIcon,
    alwaysVisible: true,
  },
  {
    name: 'Jobs',
    href: '/opportunities',
    icon: Briefcase,
    alwaysVisible: false,
    notificationKey: 'jobs',
  },
  {
    name: 'Agent',
    href: '/agent',
    icon: Bot,
    alwaysVisible: false,
  },
  {
    name: 'Sessions',
    href: '/coaching',
    icon: CalendarDays,
    alwaysVisible: true,
  },
  {
    name: 'Learn',
    href: '/learn',
    icon: GraduationCap,
    alwaysVisible: false,
    notificationKey: 'skills',
  },
  {
    name: 'Election',
    href: '/benefits/election',
    icon: Heart,
    alwaysVisible: false,
  },
  {
    name: 'Benefits',
    href: '/benefits',
    icon: LifeBuoy,
    alwaysVisible: true,
  },
  {
    name: 'Profile',
    href: '/profile',
    icon: User,
    alwaysVisible: true,
  },
]

export function BottomNav() {
  const location = useLocation()
  const { hasResume, isSeveranceUser, severancePending } = useNavVisibility()
  const notifications = useTabNotificationsStore()

  const visibleTabs = tabs.filter((tab) => {
    if (tab.href === '/coaching' && !enableCoaching) return false
    if (tab.href === '/benefits' && !enableSupport) return false
    if (tab.href === '/skills-map' && !enableSkillsMap) return false
    // The election tab only appears for severance-cohort members.
    if (tab.href === '/benefits/election') return Boolean(isSeveranceUser)
    // Before severance starts, hide the outplacement features.
    if (severancePending && OUTPLACEMENT_HREFS.has(tab.href)) return false
    if (tab.alwaysVisible) return true
    if (tab.href === '/opportunities') return hasResume
    if (tab.href === '/agent') return hasResume
    if (tab.href === '/learn') return true
    return true
  })

  return (
    <nav className="border-t bg-background flex items-center justify-around h-14 shrink-0 pb-safe">
      {visibleTabs.map((tab) => {
        const Icon = tab.icon
        const isActive =
          location.pathname === tab.href || location.pathname.startsWith(`${tab.href}/`)
        const hasNotification = tab.notificationKey && notifications[tab.notificationKey]

        return (
          <Link
            key={tab.href}
            to={tab.href}
            onClick={() => {
              if (tab.notificationKey === 'jobs') {
                notifications.setJobsNotification(false)
              }
              if (tab.notificationKey === 'skills') {
                notifications.setSkillsNotification(false)
              }
            }}
            className={cn(
              'flex flex-col items-center justify-center gap-0.5 flex-1 h-full relative',
              isActive ? 'text-blue-600' : 'text-muted-foreground',
            )}
          >
            <div className="relative">
              <Icon className="h-5 w-5" />
              {hasNotification && (
                <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-blue-600 animate-pulse" />
              )}
            </div>
            <span className="text-[10px] font-medium">{tab.name}</span>
          </Link>
        )
      })}
    </nav>
  )
}
